<script setup lang="ts">
import { computed, ref, shallowReactive, watch } from 'vue'
import { VueFlux, FluxPreloader, Img, Book } from 'vue-flux'
import { usePointerSwipe, watchImmediate } from '@vueuse/core'
import { baseUrl, delay, padWithZeros } from '@/core/utils'
import { SAMUDKOY } from '@/modules/simakatra/constants'
import { useFlipbookSamudkoy } from './FlipbookSamudkoy'
import type { Language } from '@/core/types/locale'
import 'vue-flux/style.css'

const props = defineProps<{
  lang: Language
}>()

const swipeRef = ref<HTMLElement>()
const { page, isActivated } = useFlipbookSamudkoy(swipeRef)

//-----------------------------------------------
const $vueFlux = ref()

const options = shallowReactive({
  allowToSkipTransition: true,
  autoplay: false,
  lazyLoad: true,
  lazyLoadAfter: 10,
})

const rscs = shallowReactive(
  Array.from({ length: SAMUDKOY.NUM_PAGES }, (_, i) => {
    const langPages = [1, 2, 3, 4, 61, 68, 69]
    let langPath = langPages.includes(i + 1) ? props.lang + '/' : ''
    return new Img(baseUrl(`/images/samudkoy-pages/${langPath + padWithZeros(i + 1, 2)}.png`))
  })
)

const transitions = shallowReactive([
  {
    component: Book,
    options: shallowReactive({
      totalDuration: 600,
      easing: 'ease-out',
    }),
  },
])

//-----------------------------------------------
watchImmediate(page, async (newPage, oldPage) => {
  const durationPage = newPage - (oldPage ?? newPage)

  props.lang == 'th' && console.debug({ durationPage }, { newPage, oldPage })

  if (oldPage && Math.abs(durationPage) > 1) {
    transitions[0].options.totalDuration = 200

    if (durationPage > 0) {
      for (let i = newPage - 3; i < newPage; i++) {
        props.lang == 'th' && console.debug({ i }, {isActivated: isActivated.value})        
        $vueFlux.value?.show(i - 1)
        await delay(180)
      }
    } else {
      for (let i = newPage + 3; i > newPage; i--) {
        props.lang == 'th' && console.debug({ i }, {isActivated: isActivated.value})        
        $vueFlux.value?.show(i - 1)
        await delay(180)
      }
    }

    transitions[0].options.totalDuration = 300
    $vueFlux.value?.show(newPage - 1)
  } else {
    transitions[0].options.totalDuration = 600
    $vueFlux.value?.show(newPage - 1)
  }

  // transitions[0].options.totalDuration = newPage > 10 ? 200 : 600

  //$vueFlux.value?.show(newPage - 1)
  // $vueFlux.value.show('next')
  // $vueFlux.value.show('prev')
})

//-----------------------------------------------
</script>

<template>
  <div ref="swipeRef" class="flipbook-samudkoy">
    <VueFlux :options="options" :rscs="rscs" :transitions="transitions" ref="$vueFlux">
      <template #preloader="preloaderProps">
        <FluxPreloader v-bind="preloaderProps">
          <template #default="loaderProps">
            <div v-if="loaderProps.preloading" class="custom-spinner">{{ loaderProps.pct }} %</div>
          </template>
        </FluxPreloader>
      </template>
    </VueFlux>
  </div>
</template>

<style scoped>
.flipbook-samudkoy {
  position: absolute;
  width: 1350px;
  height: 970px;
  top: 36px;
  left: 30px;
  cursor: pointer;
  /* border: 1px solid #f00; */
}

.page {
  position: absolute;
  top: 0;
  left: 0;
}

.vue-flux {
  position: absolute;
  height: 1350px;
  width: 970px;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  rotate: 90deg;
  /* border: 1px solid #0f0; */
  /* opacity: .2; */
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

.custom-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  text-align: center;
  line-height: 50px;
  margin-top: -25px;
  margin-left: -25px;
  width: 50px;
  height: 50px;
  z-index: 14;
  rotate: -90deg;

  &:before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 50px;
    height: 50px;
    margin-top: -25px;
    margin-left: -25px;
    border-radius: 50%;
    border: 1px solid #ccc;
    border-top-color: #e4af70;
    animation: spinner 0.6s linear infinite;
  }
}
</style>
